<template>
  <div>
    <app-layout>
      <template #header>
        <AppHead title="Customers" />
        <h2 class="title">
          Customers
          <jet-button :goesTo="route('shop.create')" icon="fas fa-user" class="btn-short ml-2">Create New Customer</jet-button>
        </h2>
        <flash-messages />
      </template>

      <card class="mt-3">
        <section class="row mb-3">
          <div class="form-group col-12 col-lg-3 mb-3 mb-lg-0 pr-2">
            <p class="font-weight-bold mb-1" for="location">Location:</p>
            <select v-model="form.location_id" id="location" class="form-control location-selection">
              <option disabled>Select Location</option>
              <option v-for="address in this.locations" :key="address.id" :value="address.id">{{locationName(address)}}</option>
              <option value="-1">No Location Assigned</option>
            </select>
          </div>

          <div class="col-0 col-lg-1"></div>
          
          <div class="form-row col-12 col-lg-8 mb-3 mb-lg-0">
            <p class="w-100 font-weight-bold mb-1 ml-1">Invite Customer</p>
            <div class="form-group col-md-4 mb-0">
              <input type="text" class="form-control" id="invite_phone_number" placeholder="Phone" v-model="formInvite.phone_number">
            </div>
            <div class="col-md-1 flex-center my-2 my-lg-0"><strong>or</strong></div>
            <div class="form-group col-md-4 mb-0">
              <input type="email" class="form-control" id="invite_email" placeholder="Email" v-model="formInvite.email">
            </div>
            <div class="form-group col-md-3 mb-0">
              <jet-button icon="fas fa-envelope" :disable="formInvite.processing" class="mt-3 mt-md-0 w-100" @click="inviteUser">Send Invitation</jet-button>
            </div>
          </div>
        </section>

        <section v-if="users.length" class="mt-4">
          <DataTable :value="users" 
              responsiveLayout="stack" 
              class="p-datatable-sm" 
              :rowClass="() => { return 'table-row cursor-pointer' }"
              breakpoint="992px"
              @row-click="(e) => $inertia.get(route('tu.view', e.data.id))"
              stripedRows>
              <Column field="first_name" header="Name">
                <template #body="slotProps">
                  <span v-if="slotProps.data.first_name && slotProps.data.last_name">
                    {{ slotProps.data.first_name }} {{ slotProps.data.last_name }}
                  </span>
                  <span v-else>
                    Quick invite 
                    <span v-if="slotProps.data.phone_number && slotProps.data.phone_number.length > 0">
                      to <Phone v-model="slotProps.data.phone_number" />
                    </span>            
                  </span>
                </template>
              </Column>
              <Column field="email" header="Email">
                <template #body="slotProps">
                  <Email v-model="slotProps.data.email" />
                </template>
              </Column>
              <Column field="address" header="Location">
                <template #body="slotProps">
                  {{ slotProps.data.address ? slotProps.data.address.company_name : '' }} {{ slotProps.data.address ? slotProps.data.address.address1 : '' }} {{ slotProps.data.address ? slotProps.data.address.city : '' }}, {{ slotProps.data.address ? slotProps.data.address.state : '' }} {{ slotProps.data.address ? slotProps.data.address.postcode : '' }}
                </template>
              </Column>
              
              <Column field="id" class="button-container" header="">
                <template #body="slotProps">
                  <jet-button :goesTo="route('tu.view', slotProps.data.id)" class="btn-sm" icon="fas fa-angle-right" tabindex="-1">View</jet-button>
                </template>
              </Column>
          </DataTable>
        </section>
        <p v-if="location >= 1 && !users.length" class="alert alert-info mb-0">There are no customers assigned to this location yet.</p>
      </card>

      <section v-if="invites.length > 0" class="my-4">
        <h3 class="mb-2">Invitations</h3>
        <DataTable :value="invites" 
          responsiveLayout="stack" 
          class="p-datatable-sm" tableClass="border-left border-right border-bottom"
          breakpoint="992px"
          :rowClass="() => { return 'table-row cursor-pointer' }"
          @row-click="(e) => $inertia.get(route('tu.view', e.data.id))"
          stripedRows>

          <Column field="created_at" header="Date" :sortable="true">
            <template #body="slotProps">
              {{ moment(slotProps.data.created_at).format('M/D/YY h:mm A') }}
            </template>
          </Column>
          <Column field="first_name" header="Name">
            <template #body="slotProps">
              <span v-if="slotProps.data.first_name && slotProps.data.last_name">
                {{ slotProps.data.first_name }} {{ slotProps.data.last_name }}
              </span>
            </template>
          </Column>
          <Column field="phone_number" header="Phone">
            <template #body="slotProps">
              <Phone v-model="slotProps.data.phone_number" />
            </template>
          </Column>
          <Column field="email" header="Email">
            <template #body="slotProps">
              <Email v-model="slotProps.data.email" />
            </template>
          </Column>
          <Column field="id" class="button-container" header="">
            <template #body="slotProps">
              <jet-button class="mx-2 btn-sm" icon="fas fa-paper-plane" :goesTo="route('tu.resend', slotProps.data.id)">Resend</jet-button>
              <jet-button class="mx-2 btn-sm" :goesTo="route('tu.view', slotProps.data.id)" icon="fas fa-angle-right" tabindex="-1">View</jet-button>
            </template>
          </Column>
        </DataTable>
      </section>
    </app-layout>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import AppLayout from '@/Layouts/AppLayout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'

import moment from 'moment'

export default {
  metaInfo: { title: 'Customers' },
  components: {
    AppLayout,
    Icon,
    SearchFilter,
    FlashMessages,
  },
  props: {
    users: Array,
    invites: Array,
    locations: Array,
    filters: Object,
    location: [Number, String]
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        role: this.filters.role,
        trashed: this.filters.trashed,
        location_id: this.location,
      },

      formInvite: this.$inertia.form({
        location_id: this.location,
        email: null,
        phone_number: null
      })
    }
  },
  watch: {
    form: {
      handler: throttle(function() {
        let query = pickBy(this.form)
        this.$inertia.replace(this.route('tu', Object.keys(query).length ? query : { remember: 'forget' }))
      }, 150),
      deep: true,
    },
  },
  created() {
    this.moment = moment;
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    inviteUser() {
      this.formInvite.post(this.route('users.storetuquick'))
      this.formInvite.phone_number = ''
      this.formInvite.email = ''
    },
    locationName(address) {
      if (address.company_name) return address.company_name
      else return `${address.company_name || ''} ${address.address1 || ''} ${address.address2 || ''} ${address.city || ''}, ${address.state || ''} ${address.postcode || ''}`
    }
  },
}
</script>
